import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { finalize, mergeMap } from 'rxjs/operators';
import { GetOrganizationDto } from '../dtos/getOrganizationDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';

import { FCPActivationDetails } from '../dtos/fcpActivationDetails.interface';
import { GetInvitationDto } from '../dtos/getInvitationDto.interface';
import { InvitationsDto } from '../dtos/invitationsDto.interface';
import { AdminCompanyDto, FxBillingCompanyDto, OrganizationSearchFilterDto, OrganizationSearchResultDto } from '../dtos/organizationSearchDto.interface';
import { referralDto } from '../dtos/referralDto.interface';
import { UpdateOrganizationDto } from '../dtos/updateOrganizationDto.interface';
import { CPMMState } from '../enums/cpmm-state.enum';
import { FCPState } from '../enums/subscription-state.enum';
import { CPMMActivationDetails } from '../interfaces/cpmm-activation-details.interface';
import { OrganizationSettings } from '../interfaces/organization-settings.interface';
import { UpdateOrganizationAbcpDto } from '../interfaces/update-organization-abcp-request.interface';
import { UpdateOrganizationLocaleDto } from '../interfaces/update-organization-locale-request.interface';
import { IUpdateOrganizationState } from '../interfaces/update-organization-state-request.interface';
import { UpdateOrganizationTierDto } from '../interfaces/update-organization-tier-request.interface';
import { IUploadOrganizationReportingTemplateDto } from '../interfaces/upload-organization-reporting-template-dto.interface';
import { RoutesUtil } from '../util/RoutesUtil';

@UntilDestroy()
@Injectable()
export class OrganizationService {
    /**
     *
     * @param http the http client instance
     * @param config the configurations for the library
     */
    constructor(
        private auth: Auth,
        private http: HttpClient,
        private coreProvider: AumicoCoreProvider,
        private routesUtil: RoutesUtil,
    ) {}

    getCurrentOrganization$(): Observable<GetOrganizationDto> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_getCurrentOrganization$');
        const url = this.routesUtil.getCurrentOrganizationUrl();
        return this.http
            .get<GetOrganizationDto>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getCurrentOrganization$')));
    }

    getUserById$(id: string): Observable<GetUserDto> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_getUserById$');
        const url = this.routesUtil.getUserById(id);
        return this.http
            .get<GetUserDto>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getUserById$')));
    }

    updateCurrentOrganization$(organization: { payload: UpdateOrganizationDto }): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_updateCurrentOrganization$');
        const url = this.routesUtil.getCurrentOrganizationUrl();
        return this.http
            .patch<void>(url, organization.payload)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_updateCurrentOrganization$')));
    }
    getAllOrganizations$(): Observable<GetOrganizationDto[]> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_getAllOrganizations$');
        const url = this.routesUtil.getAllOrganizationsUrl();
        return this.http
            .get<GetOrganizationDto[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getAllOrganizations$')));
    }
    createSearchIndex$(): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_createSearchIndex$');
        const url = this.routesUtil.createSearchIndex();
        return this.http
            .post<void>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_createSearchIndex$')));
    }
    getOrganizationCompanies$(organizationId: string, dataCenter: string): Observable<AdminCompanyDto[]> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_getOrganizationCompanies$');
        const url = this.routesUtil.getOrganizationCompaniesUrl(organizationId);
        return this.http
            .get<AdminCompanyDto[]>(url, { params: { dataCenter } })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getOrganizationCompanies$')));
    }

    /** Every company that ever activated functional currency, across organizations (REV-21512). */
    getFxBillingCompanies$(): Observable<FxBillingCompanyDto[]> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_getFxBillingCompanies$');
        const url = this.routesUtil.fxBillingUrl();

        return this.http
            .get<FxBillingCompanyDto[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getFxBillingCompanies$')));
    }

    /** Marks one derived billing period invoiced, or undoes that. The amount is server-side. */
    setFxBilledPeriod$(
        companyId: string,
        organizationId: string,
        dataCenter: string,
        periodStart: string,
        billed: boolean,
    ): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_setFxBilledPeriod$');
        const url = this.routesUtil.setFxBilledPeriodUrl(companyId);

        return this.http
            .patch<void>(url, { organizationId, dataCenter, periodStart, billed })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_setFxBilledPeriod$')));
    }

    reactivateCompany$(companyId: string, organizationId: string, dataCenter: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_reactivateCompany$');
        const url = this.routesUtil.reactivateCompanyUrl(companyId);
        return this.http
            .patch<void>(url, { organizationId, dataCenter })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_reactivateCompany$')));
    }

    searchOrganizations$(filter: OrganizationSearchFilterDto): Observable<OrganizationSearchResultDto[]> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_searchOrganizations$');
        const url = this.routesUtil.searchOrganizationsUrl();

        // Remove undefined properties from filter
        const cleanFilter = Object.fromEntries(Object.entries(filter).filter(([_, value]) => value !== undefined));

        return this.http
            .get<OrganizationSearchResultDto[]>(url, { params: cleanFilter as any })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_searchOrganizations$')));
    }
    updateOrganizationState$(updateOrganizationState: IUpdateOrganizationState): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_updateCurrentOrganizationState$');
        const url = this.routesUtil.updateOrganizationState(updateOrganizationState.id);
        return this.http
            .patch<void>(url, { state: updateOrganizationState.state })
            .pipe(
                finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_updateCurrentOrganizationState$')),
            );
    }

    // Used by App Admin, to update any organization tier
    updateOrganizationTier$(updateOrganizationTier: UpdateOrganizationTierDto): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_updateOrganizationTier$');
        const url = this.routesUtil.updateOrganizationTier(updateOrganizationTier.id);
        return this.http
            .patch<void>(url, { tier: updateOrganizationTier.tier })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_updateOrganizationTier$')));
    }  

    // Used by Organization Admin, to update own organization tier
    updateOrganizationSubscriptionTier$(updateOrganizationTier: UpdateOrganizationTierDto): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('updateOrganizationSubscriptionTier$');
        const url = this.routesUtil.updateOrganizationSubscriptionTier(updateOrganizationTier.id);
        return this.http
            .patch<void>(url, { tier: updateOrganizationTier.tier })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateOrganizationSubscriptionTier$')));
    }

    // No blocking request: the list shows its own busy look on the toggle.
    updateOrganizationAbcp$(updateOrganizationAbcp: UpdateOrganizationAbcpDto): Observable<void> {
        const url = this.routesUtil.updateOrganizationAbcp(updateOrganizationAbcp.id);
        return this.http.patch<void>(url, { enabled: updateOrganizationAbcp.enabled });
    }

    updateOrganizationLocale$(updateOrganizationLocale: UpdateOrganizationLocaleDto): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_updateOrganizationLocale$');
        const url = this.routesUtil.updateOrganizationLocale(updateOrganizationLocale.id);
        return this.http
            .patch<void>(url, { locale: updateOrganizationLocale.locale })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_updateOrganizationLocale$')));
    }
    updateOrganizationSettings$(organizationSettings: OrganizationSettings): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_updateOrganizationSettings$');
        const url = this.routesUtil.updateOrganizationSettings();
        return this.http
            .patch<void>(url, organizationSettings)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_updateOrganizationSettings$')));
    }
    getOrganizationSettings$(): Observable<OrganizationSettings> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_getOrganizationSettings$');
        const url = this.routesUtil.getOrganizationSettings();
        return this.http
            .get<OrganizationSettings>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getOrganizationSettings$')));
    }
    getUsers$(includeDeleted: boolean = false): Observable<GetUserDto[]> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_getUsers$');
        const url = this.routesUtil.getOrganizationUsersUrl();
        const params = includeDeleted ? { includeDeleted: 'true' } : {};
        return this.http
            .get<GetUserDto[]>(url, { params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_getUsers$')));
    }

    inviteUsers$(userEmails: { payload: string[] }): Observable<InvitationsDto> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_inviteUsers$');
        const url = this.routesUtil.getInvitationsUrl();
        return this.http
            .post<InvitationsDto>(url, { emails: userEmails.payload })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_inviteUsers$')));
    }

    inviteReferrals$(referrals: { payload: referralDto }): Observable<referralDto> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_inviteReferral$');
        const url = this.routesUtil.getReferralsInviteUrl();
        return this.http
            .post<referralDto>(url, referrals.payload)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_inviteReferral$')));
    }

    acceptReferrals$(uuid: { payload: string }): Observable<string> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_acceptReferral$');
        const url = this.routesUtil.getReferralsAcceptUrl(uuid.payload);
        return this.http
            .patch<string>(url, null)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_acceptReferral$')));
    }

    removeUsers$(userIds: { payload: string[] }): Observable<string[]> {
        const options = {
            body: { ids: userIds.payload },
        };
        this.coreProvider.dispatchBlockingRequest('UsersFe_inviteUsers$');
        const url = this.routesUtil.getOrganizationUsersUrl();
        return this.http.delete<string[]>(url, options).pipe(
            mergeMap(() => {
                return of(userIds.payload);
            }),
            finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_inviteUsers$')),
        );
    }

    updateUserRole$(userId: string, role: string): Observable<GetUserDto> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_updateUserRole$');
        const url = this.routesUtil.updateUserRole(userId);
        return this.http
            .patch<GetUserDto>(url, { role: role })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_updateUserRole$')));
    }

    bulkUpdateUserRole$(payload: { userIds: string[]; role: string }): Observable<GetUserDto[]> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_bulkUpdateUserRole$');
        const url = this.routesUtil.bulkUpdateUserRole();
        return this.http
            .patch<GetUserDto[]>(url, { userIds: payload.userIds, role: payload.role })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_bulkUpdateUserRole$')));
    }

    uploadOrganizationOrReportingTemplate$(
        uploadReportingTemplateDto: IUploadOrganizationReportingTemplateDto,
    ): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_uploadOrganizationReportingTemplate$');
        const url = this.routesUtil.getUploadOrReportingTemplateUrl(uploadReportingTemplateDto.id);
        return this.http
            .patch<void>(url, uploadReportingTemplateDto.reportingTemplateFile)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('UsersFe_uploadOrganizationReportingTemplate$'),
                ),
            );
    }

    uploadOrganizationHgbReportingTemplate$(
        uploadReportingTemplateDto: IUploadOrganizationReportingTemplateDto,
    ): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_uploadOrganizationReportingTemplate$');
        const url = this.routesUtil.getUploadHgbReportingTemplateUrl(uploadReportingTemplateDto.id);
        return this.http
            .patch<void>(url, uploadReportingTemplateDto.reportingTemplateFile)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('UsersFe_uploadOrganizationReportingTemplate$'),
                ),
            );
    }

    public setOrganizationReportingTemplateToDefault$(organizationId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Users] Set Organization Reporting Template To Default');
        const url = this.routesUtil.getSetOrganizationReportingTemplateToDefaultUrl(organizationId);
        return this.http
            .patch<void>(url, organizationId)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Users] Set Organization Reporting Template To Default',
                    ),
                ),
            );
    }

    public setOrganizationHgbReportingTemplateToDefault$(organizationId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Users] Set Organization Hgb Reporting Template To Default');
        const url = this.routesUtil.getSetOrganizationHgbReportingTemplateToDefaultUrl(organizationId);
        return this.http
            .patch<void>(url, organizationId)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Users] Set Organization Hgb Reporting Template To Default',
                    ),
                ),
            );
    }

    uploadOrganizationOrExcelTemplate$(
        uploadExcelTemplateDto: IUploadOrganizationReportingTemplateDto,
    ): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Users] Upload Organization Or Excel Template');
        const url = this.routesUtil.getUploadOrExcelTemplateUrl(uploadExcelTemplateDto.id);
        return this.http
            .patch<void>(url, uploadExcelTemplateDto.reportingTemplateFile)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Users] Upload Organization Or Excel Template'),
                ),
            );
    }

    uploadOrganizationHgbExcelTemplate$(
        uploadExcelTemplateDto: IUploadOrganizationReportingTemplateDto,
    ): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Users] Upload Organization Hgb Excel Template');
        const url = this.routesUtil.getUploadHgbExcelTemplateUrl(uploadExcelTemplateDto.id);
        return this.http
            .patch<void>(url, uploadExcelTemplateDto.reportingTemplateFile)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Users] Upload Organization Hgb Excel Template'),
                ),
            );
    }

    public setOrganizationExcelTemplateToDefault$(organizationId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Users] Set Organization Excel Template To Default');
        const url = this.routesUtil.getSetOrganizationExcelTemplateToDefaultUrl(organizationId);
        return this.http
            .patch<void>(url, organizationId)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Users] Set Organization Excel Template To Default',
                    ),
                ),
            );
    }

    public setOrganizationHgbExcelTemplateToDefault$(organizationId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Users] Set Organization Hgb Excel Template To Default');
        const url = this.routesUtil.getSetOrganizationHgbExcelTemplateToDefaultUrl(organizationId);
        return this.http
            .patch<void>(url, organizationId)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Users] Set Organization Hgb Excel Template To Default',
                    ),
                ),
            );
    }

    public activateFCPTrial$(): Observable<FCPActivationDetails> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Organization] Activate FCP Trial');
        const url = this.routesUtil.getActivateFCPTrialUrl();
        return this.http
            .patch<FCPActivationDetails>(url, null)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Organization] Activate FCP Trial'),
                ),
            );
    }
    public changeFCPState$(organizationId: string, fcpState: FCPState): Observable<FCPActivationDetails> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Organization] Activate FCP Annual Subscription');
        const url = this.routesUtil.getChangeFCPStateUrl(organizationId);
        return this.http
            .patch<FCPActivationDetails>(url, { fcpState })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Organization] Activate FCP Annual Subscription',
                    ),
                ),
            );
    }
    public deactivateFCP$(surveyData?: string): Observable<FCPActivationDetails> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Organization] Deactivate FCP Management');
        const url = this.routesUtil.getDeactivateFCPUrl();
        return this.http
            .patch<FCPActivationDetails>(url, { surveyData })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Organization] Deactivate FCP Management'),
                ),
            );
    }

    public changeCPMMState$(organizationId: string, cpmmState: CPMMState): Observable<CPMMActivationDetails> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Organization] Change CPMM State');
        const url = this.routesUtil.getChangeCPMMStateUrl(organizationId);
        return this.http
            .patch<CPMMActivationDetails>(url, { cpmmState })
            .pipe(
                finalize(() => this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Organization] Change CPMM State')),
            );
    }

    withdrawInvitation$(uuids: string[]): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_withdraw$');
        const url = this.routesUtil.withdrawInvitation();

        return this.http
            .delete<void>(url, { body: { ids: uuids } })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_withdraw$')));
    }

    resendInvitation$(uuid: string): Observable<GetInvitationDto> {
        this.coreProvider.dispatchBlockingRequest('UsersFe_resendInvitation$');
        const url = this.routesUtil.resendInvitation(uuid);

        return this.http
            .post<GetInvitationDto>(url, null)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('UsersFe_resendInvitation$')));
    }
}
